import React, { useEffect } from 'react';
import vtkImageSlice from '@kitware/vtk.js/Rendering/Core/ImageSlice';
import { resetCPRView } from '../Renderers/vtkManagers';
import type { CPRContext } from '../Renderers/vtkManagers';
import { scheduleRender } from '../renderState';

export function useCprPaintActorVisibility({
  context,
  isBoundaryEditCpr,
}: {
  context: React.MutableRefObject<CPRContext | null>;
  isBoundaryEditCpr: boolean;
}) {
  const applyBoundaryEditActorVisibility = () => {
    const actorIds = context.current?.actorIds;
    if (!actorIds) return;

    const paintActor = actorIds['CPRPaintActor'] as vtkImageSlice | undefined;
    if (!paintActor?.setVisibility) return;

    paintActor.setVisibility(!isBoundaryEditCpr);
    context.current?.renderWindow?.render?.();
  };

  useEffect(() => {
    applyBoundaryEditActorVisibility();
  }, [context.current?.actorIds['CPRPaintActor'], isBoundaryEditCpr]);

  return applyBoundaryEditActorVisibility;
}

export function useCprWindowLevelEffect({
  context,
  rendererId,
  windowLevel,
  windowWidth,
  invert,
}: {
  context: React.MutableRefObject<CPRContext | null>;
  rendererId: string;
  windowLevel: number;
  windowWidth: number;
  invert: boolean | undefined;
}) {
  useEffect(() => {
    if (!context.current) return;
    const { actorIds } = context.current;
    if (!actorIds) return;

    const actor = actorIds['CPRActor'] as vtkImageSlice;
    if (actor && actor.getProperty()) {
      actor.getProperty().setColorLevel(windowLevel);
      actor.getProperty().setColorWindow(windowWidth);
      scheduleRender(rendererId, { type: 'updateRenderWindow' });
    }
  }, [context, rendererId, windowLevel, windowWidth, invert]);
}

export function useCprResetOnResize({
  context,
  dimensions,
}: {
  context: React.MutableRefObject<CPRContext | null>;
  dimensions: { width: number; height: number };
}) {
  useEffect(() => {
    if (dimensions.width <= 0 || dimensions.height <= 0) return;
    if (!context.current?.actorIds?.['CPRActor']) return;
    requestAnimationFrame(() => {
      if (!context.current?.actorIds?.['CPRActor']) return;
      resetCPRView(context);
    });
  }, [context, dimensions.width, dimensions.height]);
}
